<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { useIdeStore } from '@/stores/ide.store'
import { useAuthStore } from '@/stores/auth.store'
import { useProjectManager } from '@/stores/projectManager.store'

import InstantShareModel from '@/components/ide/ide/settings/InstantShareModel.vue'
import ManageCollaborator from '@/components/ide/ide/settings/ManageCollaboratorModal.vue'
import EmbedShare from '@/components/ide/ide/settings/EmbedShare.vue'
import SideBarTooltip from '@/components/ide/ide/SettingsSideBarTooltip.vue'

import { IDEVIEWMODELS } from '@/utils/models'
import { GENERIC_TYPE, type IHttpStatus } from '@/services/ide/projects.service'

import AddManageCollaborator from '@/components/ide/ide/settings/AddManageCollaborator.vue'
import Button from '@/components/atoms/button/index.vue'
import ideModal from '@/components/ide/ide/IdeModal.vue'
import settingService from '@/services/ide/settings/setting.service'
import shareService from '@/services/ide/settings/share.service'
import regenerate from '@/assets/images/ide/arrow-refresh.svg'

const ideStore = useIdeStore()
const authStore = useAuthStore()
const projectManagerStore = useProjectManager()

enum ProjectShareView {
  ShareProject = 'ShareProject',
  ManageUsers = 'ManageUsers',
  EmbedShare = 'EmbedShare'
}

const view = ref<ProjectShareView>(ProjectShareView.ShareProject)
const loadUrlsInProgress = ref<boolean>(false)
const httpSuccessTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpStatus = ref<IHttpStatus>({ status: '', message: '' })

const projectUrl = ref<string | null>(null)
const copiedToClip = ref<boolean>(false)
const showCopiedMessageText = ref<string>('')
const copySuccess = ref<boolean>(false)
const timeOut = ref<NodeJS.Timeout | number | null>(null)

const projectDetails = computed(() => {
  return ideStore.isSelectedProject ? ideStore.isSelectedProject : ideStore.isProject
})
const isUserloggedIn = computed(() => {
  return authStore.isUserloggedIn
})

const projectName = computed(() =>
  isUserloggedIn.value ? `Share project: ${projectDetails.value?.name}` : 'Share project'
)

const isSettingPopupHs = computed(() => {
  return ideStore.isSettingPopupHs
})

/**
 * Open manage users view
 */
const handleOpenManageUsers = () => {
  view.value = ProjectShareView.ManageUsers
}
/**
 * Open embed share view
 */
const handleOpenEmbedShare = () => {
  view.value = ProjectShareView.EmbedShare
}
/**
 * Open share project view
 */
const handleOpenShareProject = () => {
  view.value = ProjectShareView.ShareProject
}

/**
 * @description - open  manage collaborator and closing the add manage collaborator
 */
const toggleCloseFunction = () => {
  settingService.closePopup(IDEVIEWMODELS.PROJECTSHARE)
  if (ideStore.isDefaultIde || projectManagerStore.comingFromProjectManager) {
    settingService.closePopup(IDEVIEWMODELS.PROJECTSHARE)
    settingService.openPopup(IDEVIEWMODELS.MYPROJECTS)
  }
  projectManagerStore.setComingFromProjectManager(false)
  handleOpenShareProject()
}

/**
 * Get the share url
 * @param reGenerate - to regenerate the link again
 */
const share = async (reGenerate: boolean = false) => {
  if (!reGenerate) projectUrl.value = null
  loadUrlsInProgress.value = true

  await shareService
    .instantShare()
    .then((response) => {
      projectUrl.value = response?.data?.url || null
    })
    .catch((error) => {
      httpStatus.value = {
        status: GENERIC_TYPE.ERROR,
        message: error?.response?.data?.message || 'Something went wrong. Please try again later.'
      }
    })

  loadUrlsInProgress.value = false
}

/**
 * Copy to clipboard
 * @param url string
 */
const copyToClipboard = async (url: string) => {
  window.navigator.clipboard.writeText(url).then(
    () => {
      showCopiedMessageText.value = 'Copied to clipboard!'
      copySuccess.value = true
      copiedToClip.value = true
    },
    () => {
      showCopiedMessageText.value = 'Copy to clipboard failed!'
      copySuccess.value = false
      copiedToClip.value = true
    }
  )
  if (timeOut.value) clearTimeout(timeOut.value)
  timeOut.value = setTimeout(() => {
    copiedToClip.value = false
    copySuccess.value = false
    showCopiedMessageText.value = ''
  }, 2000)
}

/**
 * Initialize the copy editor
 */
const init = () => {
  projectUrl.value = null
}

onMounted(() => {
  init()
  watch(httpStatus, () => {
    if (httpStatus.value) {
      if (httpSuccessTimeOut.value) clearTimeout(httpSuccessTimeOut.value)
      httpSuccessTimeOut.value = setTimeout(() => {
        httpStatus.value = { status: '', message: '' }
      }, 3000)
    }
  })
})

watch(isSettingPopupHs, () => {
  if (isSettingPopupHs.value === IDEVIEWMODELS.PROJECTSHARE) init()
})
</script>
<template>
  <ideModal
    :title="projectName"
    class="my-auto !w-full max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl"
    :closeFunction="toggleCloseFunction"
  >
    <!-- Instant Share -->
    <InstantShareModel v-if="!isUserloggedIn" />
    <Transition v-else name="fade" mode="out-in">
      <div v-if="view === ProjectShareView.ShareProject">
        <div class="flex items-center justify-between">
          <p class="text-mid text-base font-medium">Manage users who can access your project</p>
          <Button variant="filled" @click="handleOpenManageUsers" class="whitespace-nowrap">
            Share with users <FontAwesomeIcon icon="fa-angle-right" class="ml-3" />
          </Button>
        </div>

        <!-- Users With Access -->
        <div class="stroke-primary-default mt-2 border-t pt-2">
          <ManageCollaborator />
        </div>

        <div class="flex w-full flex-col items-start justify-start gap-2 pt-3 lg:gap-1">
          <!-- instant share  -->
          <div class="flex w-full flex-col items-start gap-2 md:flex-row md:items-center">
            <div class="flex items-center" :class="{ 'w-full': !projectUrl }">
              <Button
                variant="link"
                size="small"
                color="secondary"
                @click="share"
                v-if="!projectUrl"
                class="space-x-2 whitespace-nowrap"
              >
                {{ loadUrlsInProgress ? 'Loading...' : ' Instant share URL' }}</Button
              >
              <span class="pr-3" v-if="!projectUrl">-</span>
              <p
                class="w-fit text-sm"
                :class="projectUrl ? 'success whitespace-nowrap' : 'text-mid'"
              >
                {{ projectUrl ? 'Link generated!' : 'Generate a one-time URL to share project' }}
              </p>
            </div>
            <div class="relative flex w-full flex-grow items-center lg:w-0" v-if="projectUrl">
              <div class="hs-tooltip flex w-full overflow-hidden rounded-r-lg">
                <input
                  class="section-new-primary text-mid w-full rounded-l-lg border border-none p-2 text-sm font-light outline-none"
                  v-model="projectUrl"
                  disabled
                />

                <button :disabled="loadUrlsInProgress" @click="share(true)">
                  <img
                    :src="regenerate"
                    alt=""
                    class="hs-tooltip-toggle absolute right-28 top-3 h-4 w-4"
                    :class="{ 'rotate-infinite': loadUrlsInProgress }"
                  />
                </button>
                <SideBarTooltip content="Regenerate new instant share link" />
                <Button
                  variant="filled"
                  @click="copyToClipboard(projectUrl as string)"
                  class="mx-auto w-fit whitespace-nowrap rounded-none"
                  type="submit"
                >
                  Copy URL
                </Button>
              </div>
            </div>
          </div>
          <p
            :class="[
              'p-xsmall pt-2 ',
              { success: copySuccess, error: !copySuccess, hidden: !copiedToClip }
            ]"
          >
            {{ showCopiedMessageText }}
          </p>
          <p v-if="httpStatus.status === GENERIC_TYPE.ERROR" class="p-xsmall error pt-2">
            {{ httpStatus.message }}
          </p>
          <!-- Prompt Embed -->
          <div v-if="!ideStore.isAdvanced" class="flex w-full items-center">
            <Button variant="link" @click="handleOpenEmbedShare" class="whitespace-nowrap">
              Embed link
            </Button>
            <span class="pr-3">-</span>
            <p class="text-mid text-sm">Embed this project into your website or blog</p>
          </div>
        </div>
      </div>
      <div v-else-if="view === ProjectShareView.ManageUsers">
        <AddManageCollaborator :view="view" @handleOpenShareProject="handleOpenShareProject" />
      </div>
      <div v-else-if="view === ProjectShareView.EmbedShare">
        <div class="mb-3 flex w-full items-center justify-between">
          <span class="text-mid text-base font-medium"> Create Embed </span>
          <button
            class="flex w-fit items-center rounded-full border border-new-secondary-600 p-3"
            @click="handleOpenShareProject"
          >
            <FontAwesomeIcon
              icon="fa-angle-left"
              class="icon-button-secondary h-3 w-3"
            ></FontAwesomeIcon>
          </button>
        </div>
        <EmbedShare />
      </div>
    </Transition>
  </ideModal>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.rotate-infinite {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
